/**
 * What this browser has let a comrade do to it (MWE-27, step 1 of MN-U51).
 *
 * The person grants a comrade's presence in the call control of their own
 * microphone or camera; the portal records it (cmd/portal/grant.go) and
 * relays the comrade's requests to this browser, signed. This module keeps
 * the browser's OWN copy, and the control handler in Call.tsx refuses
 * anything outside it, whatever the portal says: consent is the person's
 * twice over. The copy lasts this page, as the grant lasts this join.
 *
 * Taking it back drops the copy FIRST, so it stops at once even if the portal
 * is slow to hear about it.
 */
import { ApiError, PREFIX } from './api';

export type Scope = 'mic' | 'camera';

const mine = new Map<string, Set<Scope>>();
const listeners = new Set<() => void>();
const changed = () => {
  for (const f of listeners) f();
};

/** Called whenever what is granted changes. Returns the unsubscribe. */
export function onGrantsChange(f: () => void): () => void {
  listeners.add(f);
  return () => listeners.delete(f);
}

/** Whether this browser has let presence control scope. */
export function granted(presence: string, scope: Scope): boolean {
  return mine.get(presence)?.has(scope) ?? false;
}

/** The grants in force here: presence identity to its scopes. */
export function grantsHere(): Map<string, Scope[]> {
  return new Map([...mine].map(([p, s]) => [p, [...s].sort()]));
}

async function call(path: string, init: RequestInit): Promise<unknown> {
  const res = await fetch(PREFIX + path, {
    credentials: 'same-origin',
    ...init,
    headers: { Accept: 'application/json', ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) detail = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(detail, res.status);
  }
  return res.json();
}

/** Let the presence (an `agent:` identity in the call) control scopes. */
export async function grantComrade(room: string, presence: string, scopes: Scope[]): Promise<void> {
  await call(`/api/calls/${encodeURIComponent(room)}/grants`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ presence, scopes }),
  });
  // Only once the portal has it: a grant it refused is not one.
  mine.set(presence, new Set(scopes));
  changed();
}

/** Take back what presence was granted, or everything for no presence. */
export async function revokeComrade(room: string, presence?: string): Promise<void> {
  if (presence) mine.delete(presence);
  else mine.clear();
  changed();
  const tail = presence ? `/${encodeURIComponent(presence)}` : '';
  await call(`/api/calls/${encodeURIComponent(room)}/grants${tail}`, { method: 'DELETE' });
}

/** Forget every grant: the call view is gone, and so is the join they were for. */
export function forgetGrants(): void {
  mine.clear();
  changed();
}
